import { useQueryClient } from '@tanstack/react-query';
import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';

import Button from '@/components/Button';
import useAlerts from '@/hooks/useAlerts';
import {
  CoverImagesProjectResource,
  StockLibraryImageResource,
} from '@/types/resources';
import { api } from '@/utils/api';

import AiImage from './AiImage';
import LightBox from './LightBox';

const shine = keyframes`
 to {
    background-position-x: -200%;
  }
`;

const ImagesPreview = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  grid-gap: 8px;
  margin: 20px 0;
`;

const ImageHolder = styled.div<{ $active?: boolean }>`
  cursor: pointer;
  border-radius: 4px;
  padding: 3px;
  border: 2px solid ${({ theme }) => theme.colors.grayscale};
  transition: border-color ease-out 0.2s;
  &:active {
    border-color: ${({ theme }) => theme.colors.gray};
  }
  ${({ $active }) =>
    $active &&
    css`
      border-color: ${({ theme }) => theme.colors.purple};
    `}
`;

const Placeholder = styled.div`
  background: #eee;
  background: linear-gradient(110deg, #ececec 8%, #f5f5f5 18%, #ececec 33%);
  background-size: 200% 100%;
  opacity: 0.7;
  height: 159px;
  animation: 1.5s ${shine} linear infinite;
`;

const StyledButton = styled(Button)`
  margin: 10px 0 20px;
`;

interface Props {
  images?: StockLibraryImageResource[];
  selectedImages: number[];
  project: CoverImagesProjectResource;
  setSelectedImages: React.Dispatch<React.SetStateAction<number[]>>;
}

const AiGeneratedImages: FC<Props> = ({
  images,
  selectedImages,
  project,
  setSelectedImages,
}) => {
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [isGenerating, setIsGenerating] = useState(false);

  const queryClient = useQueryClient();
  const alerts = useAlerts();
  const { id } = useParams();

  const onGenerateMoreImages = async () => {
    setIsGenerating(true);
    try {
      await api.post<any, { projectId: number }>(
        '/v1/cover-images/generate-more-ai-images',
        {
          project_id: id,
          article_id: project.articleId,
          is_open_list: project.isOpenList,
        },
      );

      await queryClient.invalidateQueries(['cover_images_project', { id }], {
        exact: true,
      });

      await queryClient.invalidateQueries(
        ['cover_images_project_ai_content', { id }],
        {
          exact: true,
        },
      );
    } catch (err) {
      alerts.error((err as any)?.response?.data?.message);
    }
    setIsGenerating(false);
  };

  return (
    <div>
      {!!images?.length && (
        <LightBox
          lightboxIndex={lightboxIndex}
          setLightboxIndex={setLightboxIndex}
          images={images.map((image) => image)}
        />
      )}
      <ImagesPreview>
        {images?.map((image, index) => (
          <ImageHolder
            key={image.id}
            $active={selectedImages.includes(image.id)}
            onClick={() => {
              setSelectedImages((prev) => {
                if (!prev.includes(image.id)) {
                  return [...prev, image.id];
                }
                return prev.filter((element) => element !== image.id);
              });
            }}
          >
            {image.meta?.status === 'pending' ? (
              <Placeholder />
            ) : (
              <AiImage
                image={image}
                onLightboxClick={() => setLightboxIndex(index)}
              />
            )}
          </ImageHolder>
        ))}
      </ImagesPreview>
      <StyledButton
        loading={isGenerating}
        disabled={isGenerating}
        onClick={() => onGenerateMoreImages()}
      >
        {isGenerating ? 'Generating...' : 'Generate More Images'}
      </StyledButton>
    </div>
  );
};

export default AiGeneratedImages;
